import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { Add, Delete, Edit, Person, Search } from '@mui/icons-material';
import { api } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';
import { DATE_FORMAT_HINT, formatDate, parseDateInput } from '../utils/date.js';
import { capitalizeWords, shouldCapitalizeField } from '../utils/text.js';
import { isCnicField, isPhoneField, maskCnic, maskFieldValue, maskPhone } from '../utils/mask.js';

const initialForm = {
  rfidCardNumber: '', employeeName: '', fatherName: '', address: '', gender: '',
  cnic: '', cityId: '', areaId: '', departmentId: '',
  designationId: '', mobile: '', joiningDate: formatDate(new Date()), salary: 0, status: 'Active'
};

function validateEmployeeForm(form) {
  if (!form.employeeName?.trim()) return 'Employee name is required';
  if (!form.address?.trim()) return 'Address is required';
  if (!form.gender) return 'Gender is required';
  if (!form.departmentId) return 'Department is required';
  if (!form.designationId) return 'Designation is required';
  if (!form.joiningDate) return 'Joining date is required';
  if (!parseDateInput(form.joiningDate)) return `Joining date must use ${DATE_FORMAT_HINT}`;
  return null;
}

export default function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const employeeNameRef = useRef(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const employees = useQuery({ queryKey: ['employees', search],
    queryFn: async () => (await api.get('/employees', { params: { search, status: 'All', pageSize: 1000 } })).data });
  const references = useQuery({ queryKey: ['reference-data'],
    queryFn: async () => (await api.get('/reference-data')).data });
  const cities = useQuery({ queryKey: ['resource', 'cities'],
    queryFn: async () => (await api.get('/resources/cities')).data });
  const areas = useQuery({ queryKey: ['resource', 'areas'],
    queryFn: async () => (await api.get('/resources/areas')).data });

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => employeeNameRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open, editing]);

  useEffect(() => {
    if (!photo) return undefined;
    const objectUrl = URL.createObjectURL(photo);
    setPhotoPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photo]);

  const resetForm = () => {
    setForm(initialForm);
    setPhoto(null);
    setPhotoPreview('');
    setEditing(null);
  };

  const save = useMutation({
    mutationFn: async () => {
      const validationError = validateEmployeeForm(form);
      if (validationError) {
        toast.warning(validationError);
        throw new Error(validationError);
      }
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        payload.append(key, key === 'joiningDate' ? parseDateInput(value) : value);
      });
      if (photo) payload.append('photo', photo);
      const url = editing ? `/employees/${editing.EmployeeId}` : '/employees';
      return api[editing ? 'put' : 'post'](url, payload, { skipErrorToast: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setOpen(false);
      resetForm();
      toast.success(`Employee ${editing ? 'updated' : 'saved'} successfully`);
    },
    onError: (error) => {
      if (error?.response) toast.error(formatApiError(error, `Could not ${editing ? 'update' : 'save'} employee`));
    }
  });

  const remove = useMutation({
    mutationFn: async () => api.delete(`/employees/${deleting.EmployeeId}`, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setDeleting(null);
      toast.success('Employee deactivated successfully');
    },
    onError: (error) => toast.error(formatApiError(error, 'Could not deactivate employee'))
  });

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (employee) => {
    setEditing(employee);
    setForm({
      rfidCardNumber: employee.RFIDCardNumber || '',
      employeeName: employee.EmployeeName || '',
      fatherName: employee.FatherName || '',
      address: employee.Address || '',
      gender: employee.Gender || '',
      cnic: maskFieldValue('cnic', employee.CNIC || ''),
      cityId: employee.CityId || '',
      areaId: employee.AreaId || '',
      departmentId: employee.DepartmentId || '',
      designationId: employee.DesignationId || '',
      mobile: maskFieldValue('mobile', employee.Mobile || ''),
      joiningDate: formatDate(employee.JoiningDate),
      salary: employee.Salary ?? 0,
      status: employee.Status || 'Active'
    });
    setPhoto(null);
    const apiOrigin = (import.meta.env.VITE_API_URL || 'http://localhost:5001/api').replace(/\/api\/?$/, '');
    setPhotoPreview(employee.PhotoPath ? `${apiOrigin}${employee.PhotoPath}` : '');
    setOpen(true);
  };
  const update = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (isPhoneField(name)) nextValue = maskPhone(value);
    else if (isCnicField(name)) nextValue = maskCnic(value);
    else if (shouldCapitalizeField({ name })) nextValue = capitalizeWords(value);
    setForm((current) => ({
      ...current,
      [name]: nextValue,
      ...(name === 'cityId' ? { areaId: '' } : {})
    }));
  };
  const onPhotoChange = (event) => {
    const file = event.target.files?.[0] || null;
    setPhoto(file);
  };

  return (
    <Box>
      <PageHeader title="Employee List" action={<Button
          startIcon={<Add />}
          variant="contained"
          onClick={openCreate}
          sx={{ flexShrink: 0 }}
        >
          Add Employee
        </Button>} />
      <Card>
        <Box px={1.5} py={1}><TextField size="small" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code, name, or RFID" sx={{ minWidth: { sm: 340 } }}
          slotProps={{ input: { startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> } }} /></Box>
        <TableContainer><Table>
          <TableHead sx={{ bgcolor: 'primary.main', '& .MuiTableCell-head': { color: 'primary.contrastText', fontWeight: 700 } }}>
            <TableRow><TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>RFID</TableCell><TableCell>Department</TableCell><TableCell>Designation</TableCell><TableCell>Mobile</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow>
          </TableHead>
          <TableBody>{employees.data?.items.map((row) => <TableRow key={row.EmployeeId} hover>
            <TableCell>{row.EmployeeCode}</TableCell><TableCell sx={{ fontWeight: 650 }}>{row.EmployeeName}</TableCell>
            <TableCell>{row.RFIDCardNumber || '—'}</TableCell><TableCell>{row.Department}</TableCell>
            <TableCell>{row.Designation}</TableCell><TableCell>{row.Mobile ? maskPhone(row.Mobile) : '—'}</TableCell>
            <TableCell>{row.Status}</TableCell>
            <TableCell align="right">
              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Deactivate"><IconButton size="small" color="error" onClick={() => setDeleting(row)}><Delete fontSize="small" /></IconButton></Tooltip>
            </TableCell>
          </TableRow>)}</TableBody>
        </Table></TableContainer>
        {!employees.isLoading && !employees.data?.items.length && <Typography color="text.secondary" textAlign="center" py={6}>No employees found.</Typography>}
      </Card>

      <Dialog open={open} onClose={() => { setOpen(false); resetForm(); }} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" required fullWidth name="employeeName" label="Employee name" value={form.employeeName} onChange={update} inputRef={employeeNameRef} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" fullWidth name="fatherName" label="Father name" value={form.fatherName} onChange={update} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" fullWidth name="mobile" label="Mobile" placeholder="0300-1234567" value={form.mobile} onChange={update} slotProps={{ htmlInput: { inputMode: 'numeric' } }} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" fullWidth name="cnic" label="CNIC" placeholder="12345-1234567-1" value={form.cnic} onChange={update} slotProps={{ htmlInput: { inputMode: 'numeric' } }} /></Grid>
            <Grid size={12}><TextField size="small" required fullWidth multiline minRows={2} name="address" label="Address" value={form.address} onChange={update} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" select fullWidth name="cityId" label="City" value={form.cityId} onChange={update}>
              <MenuItem value="">None</MenuItem>
              {cities.data?.filter((item) => item.isActive).map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
            </TextField></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" select fullWidth name="areaId" label="Area" value={form.areaId} onChange={update} disabled={!form.cityId}>
              <MenuItem value="">None</MenuItem>
              {areas.data?.filter((item) => item.isActive && String(item.cityId) === String(form.cityId)).map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
            </TextField></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" required select fullWidth name="gender" label="Gender" value={form.gender} onChange={update}>
              <MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem><MenuItem value="Other">Other</MenuItem>
            </TextField></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" fullWidth name="rfidCardNumber" label="RFID card number" value={form.rfidCardNumber} onChange={update} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" required select fullWidth name="departmentId" label="Department" value={form.departmentId} onChange={update}>
              {references.data?.departments.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</TextField></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField size="small" required select fullWidth name="designationId" label="Designation" value={form.designationId} onChange={update}>
              {references.data?.designations.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</TextField></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><TextField size="small" required fullWidth name="joiningDate" label="Joining date" placeholder={DATE_FORMAT_HINT} value={form.joiningDate} onChange={update} slotProps={{ inputLabel: { shrink: true }, htmlInput: { inputMode: 'numeric' } }} /></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><TextField size="small" type="number" fullWidth name="salary" label="Salary" value={form.salary} onChange={update} onFocus={(event) => event.target.select()} /></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><TextField size="small" required select fullWidth name="status" label="Status" value={form.status} onChange={update}>
              <MenuItem value="Active">Active</MenuItem><MenuItem value="Inactive">Inactive</MenuItem><MenuItem value="Terminated">Terminated</MenuItem>
            </TextField></Grid>
            <Grid size={12}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={photoPreview || undefined}
                  variant="rounded"
                  sx={{ width: 88, height: 88, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}
                >
                  {!photoPreview && <Person />}
                </Avatar>
                <Box>
                  <Button component="label" variant="outlined">
                    {photo ? 'Change photo' : 'Choose employee photo'}
                    <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhotoChange} />
                  </Button>
                  {photo && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      {photo.name}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => { setOpen(false); resetForm(); }}>Cancel</Button><Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>{editing ? 'Update Employee' : 'Save Employee'}</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Employee</DialogTitle>
        <DialogContent>
          <DialogContentText>Deactivate {deleting?.EmployeeName}? The employee history will be preserved.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => remove.mutate()}>Deactivate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
